import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "react-hot-toast";
import { API_BASE } from "../config";
import {
  Home,
  BookOpen,
  FileText,
  HelpCircle,
  PenTool,
  Layers,
  User,
  LogOut,
  Menu,
  X,
  Lock,
  RefreshCw,
  AlertCircle,
  Newspaper,
  MessageSquareMore,
} from "lucide-react";

const RENEW_AMOUNT =
  Number(process.env.REACT_APP_RENEW_AMOUNT) && Number(process.env.REACT_APP_RENEW_AMOUNT) > 0
    ? Number(process.env.REACT_APP_RENEW_AMOUNT)
    : 3999;

const UserDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("user")) || {});
  const [bannerHidden, setBannerHidden] = useState(
    localStorage.getItem("renewBannerHidden") === "true"
  );
  const [renewLoading, setRenewLoading] = useState(false);
  const [supportUnreadCount, setSupportUnreadCount] =
  useState(0);

  const getToken = () => user?.token || localStorage.getItem("token");
  const fetchSupportUnreadCount = async () => {
  try {
    const token = getToken();

    if (!token) return;

    const res = await axios.get(
      `${API_BASE}/api/user/feedback/unread-count`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    setSupportUnreadCount(
      res.data.unreadCount || 0
    );

  } catch (error) {
    console.error(
      "Unread Support Count Error:",
      error
    );
  }
};

  // ✅ Prevent false "subscription expired" flashes during logout or empty state
const expiryDate = user?.subscriptionExpiresAt
  ? new Date(user.subscriptionExpiresAt)
  : null;

const now = new Date();

const hasSubscription = !!expiryDate;

const isExpired =
  hasSubscription && expiryDate < now;

const isActive =
  user?.hasPaid && hasSubscription && expiryDate > now;

const isFreeUser =
  !hasSubscription && !user?.hasPaid;

  const lockedSections = isExpired
  ? [
      "notes",
      "editorials",
      "monthly-magazines",
      "mcqs",
      "mains",
      "test-series",
    ]
  : [];

  const navItems = [
    { name: "Dashboard", path: ".", icon: <Home size={18} />, color: "blue", key: "dashboard" },
    { name: "Study Plans", path: "study-plans", icon: <BookOpen size={18} />, color: "blue", key: "study-plans" },
    { name: "Notes", path: "notes", icon: <FileText size={18} />, color: "orange", key: "notes" },
    { name: "Hindu Editorial", path: "editorial-analysis", icon: <FileText size={18} />, color: "blue", key: "editorials" },
    { name: "Monthly Magazine", path: "monthly-magazines", icon: <Newspaper size={18} />, color: "purple", key: "monthly-magazines" },
    { name: "MCQs", path: "mcqs", icon: <HelpCircle size={18} />, color: "green", key: "mcqs" },
    { name: "Mains", path: "mains", icon: <PenTool size={18} />, color: "orange", key: "mains" },
    { name: "Test Series", path: "test-series", icon: <Layers size={18} />, color: "purple", key: "test-series" },
    { name: "Support", path: "support", icon: <MessageSquareMore size={18} />, color: "purple", key: "support",
},
    { name: "Profile", path: "profile", icon: <User size={18} />, color: "blue", key: "profile" },
  ];

  const colorClasses = {
    blue: {
      active: "bg-blue-600 text-white shadow-lg",
      hover: "hover:bg-blue-100 dark:hover:bg-blue-700/40 hover:text-blue-700",
      underline: "bg-blue-500",
    },
    orange: {
      active: "bg-orange-500 text-white shadow-lg",
      hover: "hover:bg-orange-100 dark:hover:bg-orange-700/40 hover:text-orange-600",
      underline: "bg-orange-500",
    },
    green: {
      active: "bg-green-600 text-white shadow-lg",
      hover: "hover:bg-green-100 dark:hover:bg-green-700/40 hover:text-green-600",
      underline: "bg-green-500",
    },
    purple: {
      active: "bg-purple-600 text-white shadow-lg",
      hover: "hover:bg-purple-100 dark:hover:bg-purple-700/40 hover:text-purple-600",
      underline: "bg-purple-500",
    },
  };

  const handleLogout = () => {
  navigate("/login", { replace: true });
  setTimeout(() => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    localStorage.removeItem("renewBannerHidden");
    window.dispatchEvent(new Event("userUpdated"));
  }, 50);
};

  useEffect(() => {
    const handleUserUpdate = () => {
      setUser(JSON.parse(localStorage.getItem("user")) || {});
    };
    window.addEventListener("userUpdated", handleUserUpdate);
    window.addEventListener("storage", handleUserUpdate);
    return () => {
      window.removeEventListener("userUpdated", handleUserUpdate);
      window.removeEventListener("storage", handleUserUpdate);
    };
  }, []);

  useEffect(() => {
  fetchSupportUnreadCount();

  const interval = setInterval(() => {
    fetchSupportUnreadCount();
  }, 30000);

  return () => clearInterval(interval);

}, []);

  useEffect(() => {
    if (!isExpired) return;

    const pathname = location.pathname.toLowerCase();
    const keyFromPath = (() => {
      if (pathname.includes("study-plans")) return "study-plans";
      if (pathname.includes("editorial-analysis")) return "editorials";
      if (pathname.includes("monthly-magazines")) return "monthly-magazines";
      if (pathname.includes("mcqs")) return "mcqs";
      if (pathname.includes("mains")) return "mains";
      if (pathname.includes("test-series")) return "test-series";
      if (pathname.includes("profile")) return "profile";
      return "dashboard";
    })();

    if (lockedSections.includes(keyFromPath)) {
      toast.error("Your subscription has expired. Please renew to access this section.");
      navigate("/dashboard/study-plans", { replace: true });
    }
  }, [isExpired, location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHideBanner = () => {
    setBannerHidden(true);
    localStorage.setItem("renewBannerHidden", "true");
  };

  const handleShowBannerAgain = () => {
    setBannerHidden(false);
    localStorage.setItem("renewBannerHidden", "false");
  };

  const handleRenewNow = async () => {
    try {
      if (!window.Razorpay) {
        toast.error("Payment UI failed to load. Please refresh the page.");
        return;
      }
      const key = process.env.REACT_APP_RAZORPAY_KEY;
      if (!key) {
        toast.error("Payment key missing. Set REACT_APP_RAZORPAY_KEY in .env");
        return;
      }
      const token = getToken();
      if (!token) {
        toast.error("Please log in again.");
        navigate("/login");
        return;
      }

      setRenewLoading(true);

      const orderRes = await axios.post(
        `${API_BASE}/api/payment/create-order`,
        { amount: RENEW_AMOUNT, renewal: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const { order } = orderRes.data;

      const rzp = new window.Razorpay({
        key,
        amount: order.amount,
        currency: order.currency,
        name: "UPSC Journey",
        description: "1-Year Subscription Renewal",
        order_id: order.id,
        prefill: {
          name: user?.name || user?.username || "",
          email: user?.email || "",
        },
        handler: async (response) => {
          try {
            const verifyRes = await axios.post(
              `${API_BASE}/api/payment/verify-payment`,
              {
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                renewal: true,
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );

            const updatedUser = verifyRes.data?.user;
            if (updatedUser) {
              localStorage.setItem("user", JSON.stringify(updatedUser));
            }

            localStorage.removeItem("renewBannerHidden");
            window.dispatchEvent(new Event("userUpdated"));
            toast.success("Renewal successful! Access restored.");
            setRenewLoading(false);
            navigate("/dashboard", { replace: true });
          } catch (err) {
            console.error("Verify payment failed:", err);
            toast.error(err?.response?.data?.error || "Payment verification failed");
            setRenewLoading(false);
          }
        },
        modal: { ondismiss: () => setRenewLoading(false) },
        theme: { color: "#2563eb" },
      });

      rzp.open();
    } catch (err) {
      console.error("Renewal error:", err);
      toast.error(err?.response?.data?.error || "Failed to start renewal");
      setRenewLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:to-purple-900 transition-colors duration-500">
      <div className="flex flex-1 relative">
        {/* Hamburger */}
        <div className="fixed top-4 left-4 z-50">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-full bg-white dark:bg-gray-800 shadow-lg hover:ring-2 hover:ring-blue-500 dark:hover:ring-blue-300 transition-all"
          >
            <Menu size={24} className="text-blue-600 dark:text-blue-300" />
          </button>
        </div>

        {/* Sidebar */}
        <aside
          className={`fixed inset-y-0 left-0 z-40 w-64 bg-white/95 dark:bg-gray-800/95 backdrop-blur-xl rounded-r-3xl p-6 flex flex-col border-r border-blue-100 dark:border-gray-700 shadow-2xl transform transition-transform duration-300 overflow-y-auto ${
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <div className="flex flex-col flex-1 min-h-0">
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-2xl font-semibold text-blue-700 dark:text-blue-300">
                  👋 Hi, {user?.name || user?.username || "Aspirant"}
                </h2>
                <button
                  onClick={() => setSidebarOpen(false)}
                  className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-all"
                >
                  <X size={24} className="text-blue-700 dark:text-blue-300" />
                </button>
              </div>

              {/* 🔴 Subscription expired badge */}
              {isExpired && hasSubscription && (
                <div className="flex items-center gap-2 bg-red-100 dark:bg-red-800 text-red-700 dark:text-red-300 px-3 py-1 rounded-full text-sm font-medium w-fit">
                  <AlertCircle size={16} /> Subscription Expired
                </div>
              )}
            </div>

            {/* Navigation */}
            <nav className="flex flex-col gap-3 flex-1 overflow-y-auto pr-1">
              {navItems.map(({ name, path, icon, color, key }) => {
                const isLocked = isExpired && hasSubscription && lockedSections.includes(key);
                return (
                  <div key={name} className="relative">
                    <NavLink
                      to={isLocked ? "#" : path}
                      end={path === "."}
                      onClick={(e) => {
                        if (isLocked) {
                          e.preventDefault();
                          toast.error("🔒 Locked — renew to access.");
                        } else {
                          setSidebarOpen(false);
                        }
                      }}
                      className={({ isActive }) =>
                        `group flex items-center gap-3 px-4 py-3 rounded-xl text-lg font-medium transition-all duration-300 relative ${
                          isActive && !isLocked
                            ? colorClasses[color].active
                            : `text-gray-700 dark:text-gray-300 ${
                                colorClasses[color].hover
                              } ${isLocked ? "opacity-50 cursor-not-allowed" : ""}`
                        }`
                      }
                    >
                      <span className="transition-transform duration-300 group-hover:scale-110">
                        {icon}
                      </span>
                      <span className="relative flex items-center gap-2">
                        {name}
                        <div className="flex items-center gap-2">

  {isLocked && (
    <Lock
      size={14}
      className="text-red-500"
    />
  )}

  {key === "support" &&
    supportUnreadCount > 0 && (
      <div
        className="
          min-w-[20px]
          h-5
          px-1.5
          rounded-full
          bg-gradient-to-r
          from-pink-500
          to-purple-500
          text-white
          text-[10px]
          font-bold
          flex
          items-center
          justify-center
          shadow-lg
          shadow-purple-500/30
          animate-pulse
        "
      >
        {supportUnreadCount > 99
          ? "99+"
          : supportUnreadCount}
      </div>
    )}
</div>
                        <span
                          className={`absolute left-0 -bottom-1 w-0 h-0.5 ${
                            colorClasses[color].underline
                          } group-hover:w-full transition-all duration-300`}
                        />
                      </span>
                    </NavLink>
                  </div>
                );
              })}
            </nav>

            {/* ✅ Logout — ALWAYS VISIBLE */}
            <div className="mt-8 pt-4 border-t border-gray-300 dark:border-gray-700">
              <motion.button
                onClick={handleLogout}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full flex justify-center items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-xl shadow-md"
              >
                <LogOut size={20} />
                Logout
              </motion.button>
            </div>
          </div>
        </aside>

        {/* Overlay */}
        {sidebarOpen && (
          <div
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm transition-opacity duration-300"
          />
        )}

        {/* Main Content */}
        <main className="flex-1 p-4 md:p-8 overflow-auto bg-white dark:bg-gray-900 transition-colors duration-500">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="max-w-7xl mx-auto min-h-[80vh]"
          >
            {/* Renewal Banner */}
            {isExpired && hasSubscription && !bannerHidden && (
              <div className="mb-6 p-4 bg-yellow-100 text-yellow-800 border border-yellow-300 rounded-lg shadow-sm flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  ⚠️ Your premium subscription expired on{" "}
                  <strong>
                    {user?.subscriptionExpiresAt
                      ? new Date(user.subscriptionExpiresAt).toLocaleDateString()
                      : "—"}
                  </strong>
                  . Please renew to unlock premium features.{" "}
                  <span className="font-medium">Study Plans remain available for lifetime.</span>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={handleRenewNow}
                    disabled={renewLoading}
                    className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition disabled:opacity-60"
                  >
                    {renewLoading ? "Processing…" : `🔄 Renew Now (₹${RENEW_AMOUNT})`}
                  </button>
                  <button
                    onClick={handleHideBanner}
                    className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                  >
                    Hide
                  </button>
                </div>
              </div>
            )}

            {/* Show Renewal Option Button */}
            {isExpired && hasSubscription && bannerHidden && (
              <div className="mb-4 flex justify-center">
                <button
                  onClick={handleShowBannerAgain}
                  className="flex items-center gap-2 bg-blue-100 text-blue-700 hover:bg-blue-200 px-4 py-2 rounded-md font-medium transition"
                >
                  <RefreshCw size={16} /> Show Renewal Option
                </button>
              </div>
            )}

            <Outlet />
          </motion.div>
        </main>
      </div>

      {/* Footer */}
      <footer className="w-full bg-gray-100 dark:bg-gray-800 text-center text-sm text-gray-600 dark:text-gray-400 p-4 border-t border-gray-300 dark:border-gray-700">
        © {currentYear} UPSC Journey. All rights reserved.
      </footer>
    </div>
  );
};

export default UserDashboard;
